import { useState } from 'react';
import { useConfirm } from '../components/Confirm.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatINR, paymentMethodOptions } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { ReferredByPicker } from '../components/ReferredByPicker.js';
import { APPROVAL_TYPE_LABELS as TYPE_LABELS } from '../labels.js';
import { useAuth } from '../auth/AuthContext.js';

interface ApprovalReq {
  id: number;
  request_no: string;
  request_type: string;
  entity_type: string | null;
  entity_id: string | null;
  level: number;
  max_levels: number;
  status: string;
  metadata: Record<string, unknown>;
  canAct: boolean;
  selfApproval?: boolean;
  subject?: string;
  amount?: number | null;
  maker_name?: string | null;   // who RAISED the request (e.g. created the customer)
  /** When it was raised. Shown on every approval so "who sent this, and when"
   *  is answerable from the queue (owner 2026-09-07). */
  created_at?: string | null;
}

/**
 * "4 Sep 2026, 3:42 pm" — the date AND the time, because the question being
 * answered is how long a request has been sitting, and a bare date cannot
 * distinguish this morning from three days ago at closing time.
 *
 * Rendered in the browser's own locale/zone: the reader is at a branch in IST
 * and the stored value is UTC, so formatting the raw string would show the
 * wrong hour.
 */
function raisedAt(v: unknown): string | null {
  const t = v ? new Date(String(v)) : null;
  if (!t || Number.isNaN(t.getTime())) return null;
  return t.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/** How long ago, for the cases where the absolute time is not the point. */
function agoFrom(v: unknown): string | null {
  const t = v ? new Date(String(v)) : null;
  if (!t || Number.isNaN(t.getTime())) return null;
  const mins = Math.floor((Date.now() - t.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** A fact value that is a document/endpoint URL (rendered as an openable link,
 *  not bare text) — an in-app API path or an absolute http(s) URL. */
function isDocUrl(v: string): boolean {
  return typeof v === 'string' && (v.startsWith('/api/') || /^https?:\/\//.test(v));
}

/** Human title for a request card, e.g. "NCD_27 · Activation" for a batch. */
function requestTitle(r: ApprovalReq): string {
  const label = TYPE_LABELS[r.request_type] ?? r.request_type;
  const series = r.metadata.series_code ? String(r.metadata.series_code) : null;
  return series ? `${series} · ${label}` : label;
}

/** Expanded row: full request payload from GET /api/approvals/:id. */
interface CoveredRow {
  application_no: string; customer: string; customer_code: string;
  amount: string; date_money_received: string | null; series_code: string;
}

function Detail({ id, canAct, selfApproval, actionLabel, onDone }: { id: number; canAct: boolean; selfApproval?: boolean; actionLabel: string; onDone: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['approval', id],
    queryFn: () => api.get<{ request: Record<string, unknown>; covered: CoveredRow[] | null;
      detail: { subject: string; amount: number | null; facts: Array<{ label: string; value: string }> };
      editable: Editable | null }>(`/api/approvals/${id}`),
  });
  // Allotment date is editable at approval time (backdatable). null = "use the
  // maker's date" until the approver changes it.
  const [allotDateOverride, setAllotDateOverride] = useState<string | null>(null);
  if (isLoading) return <div className="text-xs text-text-muted px-4 pb-3">Loading detail…</div>;
  const r = data?.request ?? {};
  const covered = data?.covered ?? null;
  const facts = data?.detail?.facts ?? [];
  const total = (covered ?? []).reduce((s, c) => s + Number(c.amount), 0);
  const isAllotment = String(r.request_type ?? '') === 'allotment_batch';
  const metaAllotDate = String((r.metadata as Record<string, unknown> | undefined)?.allotment_date ?? '').slice(0, 10);
  const allotDate = allotDateOverride ?? metaAllotDate;
  return (
    <div className="px-4 pb-4">
      {covered && (
        <div className="bg-bg rounded p-3 mb-2">
          <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">
            {covered.length} investment{covered.length === 1 ? '' : 's'} in this batch · {formatINR(total)}
          </div>
          {covered.length === 0 ? (
            <div className="text-xs text-text-muted">Nothing left in this batch — the covered investments were already processed.</div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-1.5 pr-3 font-semibold text-text-label">Customer</th>
                    <th className="py-1.5 pr-3 font-semibold text-text-label">App no</th>
                    <th className="py-1.5 pr-3 font-semibold text-text-label">Received</th>
                    <th className="py-1.5 font-semibold text-text-label text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {covered.map((c) => (
                    <tr key={c.application_no} className="border-b border-border last:border-0">
                      <td className="py-1.5 pr-3">{c.customer} <span className="text-text-muted font-mono">{c.customer_code}</span></td>
                      <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{c.application_no}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{c.date_money_received ? String(c.date_money_received).slice(0, 10) : '—'}</td>
                      <td className="py-1.5 text-right mono">{formatINR(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {(facts.length > 0 || covered) && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs bg-bg rounded p-3 mb-2">
          {covered && (
            <span className="contents">
              <dt className="text-text-muted">Total amount</dt>
              <dd className="m-0 font-semibold break-words mono">{formatINR(total)}</dd>
            </span>
          )}
          {facts.map((f) => (
            <span key={f.label} className="contents">
              <dt className="text-text-muted">{f.label}</dt>
              <dd className="m-0 font-medium break-words">
                {/* A document fact (e.g. the signed locker agreement) is a URL —
                    render it as a link the checker can open and review before
                    approving, not a bare path (owner 2026-08-29). */}
                {isDocUrl(f.value)
                  ? <a href={f.value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {f.value.toLowerCase().endsWith('.pdf') ? 'View PDF ↗' : 'Open ↗'}
                    </a>
                  : f.value}
              </dd>
            </span>
          ))}
        </dl>
      )}
      {isAllotment && canAct && (
        <div className="mb-2 bg-bg rounded p-3">
          <label className="block text-xs font-semibold text-text-label mb-1">Allotment date</label>
          <input type="date" value={allotDate} onChange={(e) => setAllotDateOverride(e.target.value)}
            className="px-2 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary" />
          <p className="text-[11px] text-text-muted mt-1">You can set a past date — e.g. the date the series was actually allotted.</p>
        </div>
      )}
      {data?.editable
        ? <EditableInvestment ed={data.editable} id={id} canAct={canAct} selfApproval={selfApproval} actionLabel={actionLabel} onDone={onDone} />
        : canAct && <ConfirmApproval id={id} label={actionLabel} selfApproval={selfApproval}
            extraFields={isAllotment ? { allotment_date: allotDate } : undefined} onDone={onDone} />}
    </div>
  );
}

/** CXO waive/discount of a pending premature penalty, shown on the approval card. */
function PrematureWaive({ redemptionId, penalty, netPayment, waived, onDone }: {
  redemptionId: number; penalty: number; netPayment: number; waived: boolean; onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const apply = useMutation({
    mutationFn: (newPenalty: number) => api.post(`/api/redemptions/${redemptionId}/waive-penalty`, { new_penalty: newPenalty, reason: reason.trim() }),
    onSuccess: () => { setOpen(false); setAmt(''); setReason(''); setErr(''); onDone(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed'),
  });
  const inr = (n: number) => formatINR(n);
  return (
    <div className="px-4 pb-3 -mt-1">
      <div className="text-xs text-text-muted">
        Penalty <span className="font-semibold text-text mono">{inr(penalty)}</span> · Net payable <span className="font-semibold text-text mono">{inr(netPayment)}</span>
        {waived && <span className="ml-2 text-success">· penalty adjusted</span>}
        {' '}<button onClick={() => setOpen((s) => !s)} className="text-primary hover:underline">{open ? 'Cancel' : 'Waive / discount penalty'}</button>
      </div>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2 bg-bg rounded p-2.5">
          <button disabled={apply.isPending || !reason.trim()} onClick={() => apply.mutate(0)}
            className="text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40 hover:bg-primary-hover">Waive fully (₹0)</button>
          <span className="text-xs text-text-muted">or set to</span>
          <input className="w-28 px-2 py-1 text-xs border border-border-strong rounded" type="number" placeholder="₹ penalty" value={amt} onChange={(e) => setAmt(e.target.value)} />
          <input className="flex-1 min-w-[10rem] px-2 py-1 text-xs border border-border-strong rounded" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button disabled={apply.isPending || !amt || !reason.trim() || Number(amt) > penalty} onClick={() => apply.mutate(Number(amt))}
            className="text-xs border border-border-strong rounded px-3 py-1.5 disabled:opacity-40 hover:border-primary">Apply discount</button>
          {err && <span className="text-xs text-danger w-full">{err}</span>}
        </div>
      )}
    </div>
  );
}

/** App/LockerHub investment notice: already live — lets the admin assign a
 * staff/agent when the customer gave no referral code. */
function AppInvestmentNotice({ appId, amount, needsAttribution, referredBy, onDone }: {
  appId: number; amount: number | null; needsAttribution: boolean; referredBy: string | null; onDone: () => void;
}) {
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [assigned, setAssigned] = useState<string | null>(null);
  type Payee = { kind: string; source: 'agents' | 'users'; id: number; code: string | null; full_name: string };
  const search = useQuery({
    queryKey: ['payee-search', q],
    queryFn: () => api.get<{ rows: Payee[] }>(`/api/agents/payee-search?q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim().length >= 2,
  });
  const assign = useMutation({
    // 8 of 30 selectable staff have NO code, and posting that empty string was
    // rejected as "Invalid request" — which is what the operator saw. Fall back
    // to the name, which is what resolveReferrer matches on next, and send the
    // row we actually clicked so the server can refuse a name that identifies
    // somebody else (owner 2026-09-12).
    mutationFn: (p: Payee) => api.post(`/api/applications/${appId}/attribute-referrer`, {
      payee: (p.code ?? '').trim() || p.full_name,
      payee_source: p.source, payee_id: p.id,
    }),
    onSuccess: (_r, p) => { setAssigned(p.full_name); setQ(''); setErr(''); onDone(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed'),
  });
  const showAssign = (needsAttribution || !referredBy) && !assigned;
  return (
    <div className="px-4 pb-3 -mt-1">
      <div className="text-xs text-text-muted">
        {amount != null && <span>Live for the customer · <span className="font-semibold text-text mono">{formatINR(amount)}</span>. </span>}
        {assigned ? <span className="text-success">Assigned to {assigned}.</span>
          : referredBy ? <span>Referred by <span className="font-mono">{referredBy}</span>.</span>
          : <span className="text-danger">No referral code — assign a staff/agent, then acknowledge.</span>}
      </div>
      {showAssign && (
        <div className="mt-2 bg-bg rounded p-2.5">
          <input className="w-full px-2 py-1 text-xs border border-border-strong rounded" placeholder="Search staff or agent by name / code…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          {q.trim().length >= 2 && (search.data?.rows.length ?? 0) > 0 && (
            <div className="mt-1.5 flex flex-col gap-1 max-h-40 overflow-y-auto">
              {search.data!.rows.map((p) => (
                <button key={`${p.source}-${p.id}`} disabled={assign.isPending} onClick={() => assign.mutate(p)}
                  className="text-left text-xs px-2 py-1 rounded hover:bg-surface border border-transparent hover:border-border">
                  {p.full_name}{' '}
                  {p.code
                    ? <span className="font-mono text-text-muted">{p.code}</span>
                    // Said out loud: an empty gap here is why the row looked
                    // identical to a working one right up to the error.
                    : <span className="text-text-muted italic">no code</span>}{' '}
                  <span className="text-text-muted">· {p.kind}</span>
                </button>
              ))}
            </div>
          )}
          {q.trim().length >= 2 && (search.data?.rows.length ?? 0) === 0 && !search.isLoading && (
            <div className="text-xs text-text-muted mt-1">No staff or agent matches “{q.trim()}”.</div>
          )}
          {err && <div className="text-xs text-danger mt-1">{err}</div>}
        </div>
      )}
    </div>
  );
}

/** Maker step for locker cheques: "funds cleared" raises an Admin/CXO approval
 *  (which then clears + settles on LockerHub), "did not clear" bounces it. All
 *  cheque handling lives on this page (owner 2026-08-07); the raised approval
 *  appears in the queue below. Hidden when there are no pending cheques. */
function ChequeClearanceMaker({ onMsg }: { onMsg: (m: string) => void }) {
  const { can } = useAuth();
  const { promptText } = useConfirm();
  const qc = useQueryClient();
  const cheques = useQuery({ queryKey: ['locker-cheques-pending'], queryFn: () => api.get<{ rows: any[] }>('/api/lockers/cheques?status=Pending') });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['locker-cheques-pending'] }); qc.invalidateQueries({ queryKey: ['approvals'] }); qc.invalidateQueries({ queryKey: ['nav-badges'] }); };

  const submit = useMutation({
    mutationFn: async (id: number) => {
      const on = await promptText({ title: 'Mark this cheque cleared', label: 'Date the funds cleared in the bank', inputType: 'date', defaultValue: new Date().toISOString().slice(0, 10), minLength: 10, confirmLabel: 'Next' });
      if (!on) return null;
      const ref = await promptText({ title: 'Bank reference', body: 'Optional — leave blank if you do not have one.', label: 'Reference', minLength: 0, confirmLabel: 'Send for approval' });
      if (ref === null) return null;
      return api.post(`/api/lockers/cheques/${id}/clear`, { cleared_on: on, reference: ref.trim() || undefined });
    },
    onSuccess: (r) => { if (r) { onMsg('Sent to Approvals — an Admin/CXO confirms it, which clears the cheque and settles the leg on LockerHub.'); refresh(); } },
    onError: (e) => onMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  const bounce = useMutation({
    mutationFn: async (id: number) => {
      const reason = await promptText({ title: 'Cheque did not clear', body: 'Bounced, or withdrawn by the customer?', label: 'Reason', confirmLabel: 'Record', danger: true });
      if (!reason) return null;
      return api.post(`/api/lockers/cheques/${id}/bounce`, { reason });
    },
    onSuccess: (r) => { if (r) { onMsg(''); refresh(); } },
    onError: (e) => onMsg(e instanceof ApiError ? e.message : 'Failed'),
  });

  const rows = cheques.data?.rows ?? [];
  if (!rows.length) return null;
  const canAct = can('applications:confirm-collection');

  return (
    <div className="mb-5">
      <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Cheques awaiting clearance <span className="text-text-muted">({rows.length})</span></h2>
      <p className="text-xs text-text-muted -mt-1 mb-2">A cheque cleared the bank? Mark it here — it goes to an Admin/CXO for approval, and only then settles the locker leg on LockerHub.</p>
      <div className="bg-surface border border-border rounded-lg shadow-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs text-text-label uppercase tracking-wide border-b border-border">
              <th className="py-2 px-4">Customer</th><th className="py-2 pr-3">Locker app</th><th className="py-2 pr-3">Leg</th>
              <th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Cheque</th><th className="py-2 pr-3">Received</th><th className="pr-4" />
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.id} className="border-b border-border last:border-0">
                <td className="py-2 px-4">{q.customer_name ?? '—'} <span className="font-mono text-xs text-text-muted">{q.customer_code ?? ''}</span></td>
                <td className="py-2 pr-3 font-mono text-xs">{q.lockerhub_application_id}</td>
                <td className="py-2 pr-3">{q.leg}</td>
                <td className="py-2 pr-3 text-right mono">{formatINR(q.amount)}</td>
                <td className="py-2 pr-3 font-mono text-xs">{q.cheque_no}{q.bank_name ? ` · ${q.bank_name}` : ''}</td>
                <td className="py-2 pr-3 text-xs text-text-muted">{q.received_on}</td>
                <td className="py-2 pr-4 text-right whitespace-nowrap">
                  {q.approval_request_id
                    ? <span className="text-xs text-warn">awaiting approval below</span>
                    : canAct ? (
                      <>
                        <button className="text-xs text-primary hover:underline mr-3" disabled={submit.isPending} onClick={() => submit.mutate(q.id)}>Funds cleared</button>
                        <button className="text-xs text-danger hover:underline" disabled={bounce.isPending} onClick={() => bounce.mutate(q.id)}>Did not clear</button>
                      </>
                    ) : <span className="text-xs text-text-muted">awaiting confirmation</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ApprovalsPage() {
  const { promptText } = useConfirm();
  const qc = useQueryClient();
  const [msg, setMsg] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const { data, isLoading, error } = useQuery({ queryKey: ['approvals'], queryFn: () => api.get<{ rows: ApprovalReq[] }>('/api/approvals/queue') });
  const uiConfig = useQuery({ queryKey: ['ui-config'], queryFn: () => api.get<{ values: Record<string, unknown> }>('/api/settings/ui-config') });
  const waiverEnabled = uiConfig.data?.values['redemption.premature_penalty_waiver_enabled'] !== false;

  const act = useMutation({
    mutationFn: (v: { id: number; action: 'approve' | 'reject'; reason?: string }) =>
      api.post(`/api/approvals/${v.id}/${v.action}`, v.action === 'reject' ? { reason: v.reason } : {}),
    onSuccess: () => { setMsg(''); qc.invalidateQueries({ queryKey: ['approvals'] }); qc.invalidateQueries({ queryKey: ['nav-badges'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });

  if (isLoading) return <div className="text-text-muted">Loading approvals…</div>;
  if (error) return <div className="text-danger">Failed to load approvals.</div>;

  return (
    <div className="w-full">
      <h1 className="text-xl font-bold tracking-tight m-0">Approvals</h1>
      <p className="text-sm text-text-muted mt-1 mb-5">Requests waiting on a checker. You can't approve your own submissions.</p>
      {msg && <div className="text-xs text-danger mb-3">{msg}</div>}
      <ChequeClearanceMaker onMsg={setMsg} />

      {groupsOf(data!.rows).map(([type, rows]) => (
      <div key={type} className="mb-5">
        <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">
          {TYPE_LABELS[type] ?? type} <span className="text-text-muted">({rows.length})</span>
        </h2>
        <div className="bg-surface border border-border rounded-lg shadow-card divide-y divide-border">
        {rows.map((r) => (
          <div key={r.id}>
            <div className="p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  <span>{requestTitle(r)}</span>
                  {r.amount != null && <span className="mono text-primary">{formatINR(r.amount)}</span>}
                  {r.max_levels > 1 && <span className="text-xs text-text-muted font-normal">level {r.level} of {r.max_levels}</span>}
                </div>
                {r.subject && <div className="text-sm text-text mt-0.5 truncate">{r.subject}</div>}
                <div className="text-xs text-text-muted font-mono mt-0.5">
                  {r.request_no}
                  {r.maker_name && <span className="font-sans"> · raised by {r.maker_name}</span>}
                  {/* When it was sent (owner 2026-09-07). The absolute time is
                      what a queue needs — "3d ago" alone cannot be quoted back
                      to anyone — with the relative age beside it so a request
                      that has been sitting stands out. */}
                  {raisedAt(r.created_at) && (
                    <span className="font-sans"> · {raisedAt(r.created_at)}
                      {agoFrom(r.created_at) && <span className="text-text-muted"> ({agoFrom(r.created_at)})</span>}
                    </span>
                  )}
                </div>
              </div>
              {!r.canAct && (
                <button onClick={() => setOpenId(openId === r.id ? null : r.id)} className="text-xs text-primary hover:underline">
                  {openId === r.id ? 'Hide' : 'Details'}
                </button>
              )}
              {r.canAct ? (
                <div className="flex gap-2">
                  <button onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    className="text-xs bg-primary text-white rounded px-3 py-1.5 hover:bg-primary-hover">
                    {openId === r.id ? 'Close' : (['app_investment', 'customer_creation'].includes(r.request_type) ? 'Review & acknowledge' : 'Review & approve')}
                  </button>
                  {r.request_type !== 'app_investment' && (
                    <button onClick={async () => {
                      const reason = await promptText({
                        title: 'Reject this request?',
                        body: 'The maker will see your reason. Nothing is settled.',
                        label: 'Reason for rejection', confirmLabel: 'Reject', danger: true,
                      });
                      if (reason) act.mutate({ id: r.id, action: 'reject', reason });
                    }} className="text-xs border border-border text-danger rounded px-3 py-1.5 hover:bg-[color:var(--danger-bg)]">Reject</button>
                  )}
                </div>
              ) : (
                <span className="text-xs text-text-muted italic">awaiting another checker</span>
              )}
            </div>
            {r.canAct && r.request_type === 'premature_redemption' && waiverEnabled && (
              <PrematureWaive
                redemptionId={Number(r.metadata.redemption_id)}
                penalty={Number(r.metadata.penalty ?? 0)}
                netPayment={Number(r.metadata.net_payment ?? 0)}
                waived={r.metadata.penalty_waived === true}
                onDone={() => qc.invalidateQueries({ queryKey: ['approvals'] })}
              />
            )}
            {r.canAct && r.request_type === 'app_investment' && r.entity_id && (
              <AppInvestmentNotice
                appId={Number(r.entity_id)}
                amount={r.metadata.amount != null ? Number(r.metadata.amount) : null}
                needsAttribution={r.metadata.needs_attribution === true}
                referredBy={r.metadata.referred_by ? String(r.metadata.referred_by) : null}
                onDone={() => qc.invalidateQueries({ queryKey: ['approvals'] })}
              />
            )}
            {openId === r.id && (
              <Detail id={r.id} canAct={r.canAct} selfApproval={r.selfApproval}
                actionLabel={['app_investment', 'customer_creation'].includes(r.request_type) ? 'Confirm acknowledgement' : 'Confirm approval'}
                onDone={() => { setOpenId(null); qc.invalidateQueries({ queryKey: ['approvals'] }); }} />
            )}
          </div>
        ))}
        </div>
      </div>
      ))}
      {data!.rows.length === 0 && (
        <div className="bg-surface border border-border rounded-lg shadow-card p-6 text-center text-text-muted">
          Nothing awaiting your approval.
        </div>
      )}
    </div>
  );
}

/** Queue grouped by request type so it's obvious what kinds of approval are
 * pending and how many of each. Order follows the queue (newest first). */
function groupsOf(rows: ApprovalReq[]): Array<[string, ApprovalReq[]]> {
  const by = new Map<string, ApprovalReq[]>();
  for (const r of rows) {
    const g = by.get(r.request_type) ?? [];
    g.push(r);
    by.set(r.request_type, g);
  }
  return [...by.entries()];
}

interface Credit {
  id: number;
  amount: number;
  date_money_received: string | null;
  collection_method: string | null;
  collection_reference: string | null;
  has_receipt: boolean;
}

interface Editable {
  application_id: number;
  customer_id: number;
  has_receipt: boolean;
  /** Every credit making up the investment. More than one = clubbed. */
  credits?: Credit[];
  readonly: Record<string, string>;
  fields: {
    total_amount: number; date_money_received: string; collection_method: string;
    collection_reference: string; referred_by_text: string; collection_bank_id: number | null;
  };
}

/** NCDs are issued in whole ₹1,00,000 units (owner spec 2026-07-23). */
const LAKH = 100000;

const RO_LABELS: Array<[string, string]> = [
  ['customer', 'Customer'], ['pan', 'PAN'], ['phone', 'Phone'], ['dob', 'Date of birth'],
  ['application_no', 'Reference no.'],
  ['series', 'Series'], ['scheme', 'Scheme'], ['rate', 'Rate'], ['tenure', 'Tenure'],
  ['created_at', 'Entered on'], ['interest_start', 'Interest starts'], ['status', 'Current status'],
];
const FIELD_LABELS: Array<[keyof Editable['fields'], string, string]> = [
  ['total_amount', 'Investment amount', 'number'],
  ['date_money_received', 'Money received on', 'date'],
  ['collection_method', 'Payment method', 'select'],
  ['collection_reference', 'Payment reference / UTR', 'text'],
  ['collection_bank_id', 'Credited to (Dhanam account)', 'bank'],
  ['referred_by_text', 'Referred by (code or name)', 'text'],
];

/** The maker's input, pre-filled and correctable by the approver. Approving with
 * corrections applies them to the investment first, then approves. */
function EditableInvestment({ ed, id, canAct, selfApproval, actionLabel, onDone }: { ed: Editable; id: number; canAct: boolean; selfApproval?: boolean; actionLabel: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState(ed.fields);
  const [err, setErr] = useState('');
  const [selfReason, setSelfReason] = useState('');
  // The Dhanam accounts money can be credited to (the collection accounts).
  const banks = useQuery({
    queryKey: ['collection-banks'],
    queryFn: () => api.get<{ rows: Array<{ id: number; account_label: string; account_number: string | null; is_collection_account: boolean; is_active: boolean }> }>('/api/banks'),
  });
  const collectionBanks = (banks.data?.rows ?? []).filter((b) => b.is_collection_account && b.is_active);
  const blocked = !!selfApproval && selfReason.trim().length < 3;
  const dirty = (Object.keys(ed.fields) as Array<keyof Editable['fields']>).some((k) => String(f[k] ?? '') !== String(ed.fields[k] ?? ''));
  // Approval is the go-live gate: nothing becomes Active off-denomination,
  // whatever created it (staff, app, or an inbound LockerHub write). Mirrors
  // the server rule so the checker sees it before clicking, not after.
  const amt = Number(f.total_amount ?? 0);
  const ticketOk = amt >= LAKH && Math.round(amt * 100) % (LAKH * 100) === 0;
  const approveWithEdits = useMutation({
    mutationFn: () => api.post(`/api/approvals/${id}/approve`, { extra: { edits: f, ...(selfApproval ? { self_approval_reason: selfReason.trim() } : {}) } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval', id] }); onDone(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed to approve'),
  });
  const inp = 'px-2.5 py-1.5 text-xs border border-border-strong rounded outline-none focus:border-primary w-full';
  return (
    <div className="bg-bg rounded p-3">
      <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Investment details</div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs mb-3">
        {RO_LABELS.filter(([k]) => ed.readonly[k]).map(([k, label]) => (
          <span key={k} className="contents">
            <dt className="text-text-muted">{label}</dt>
            <dd className="m-0 font-medium break-words">{ed.readonly[k]}</dd>
          </span>
        ))}
      </dl>
      {/* How the money actually arrived. An investment can be paid in parts and
          clubbed — ₹50,000 + ₹25,000 + ₹25,000 = one ₹1,00,000 NCD — and the
          fields below carry only the FIRST credit's date, method and reference.
          Without this the approver signs off ₹1,00,000 having seen evidence for
          ₹50,000, with no sign the other payments exist.
          One credit shows nothing: the fields already say it all. */}
      {(ed.credits?.length ?? 0) > 1 && (
        <div className="mb-3 border border-warn/40 rounded overflow-hidden">
          <div className="px-3 py-2 bg-[color:var(--warn-bg)] text-warn text-xs font-semibold">
            Paid in {ed.credits!.length} parts — check each one, not just the total
          </div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-border text-text-label">
                <th className="py-1.5 px-3 text-left font-semibold">#</th>
                <th className="py-1.5 px-3 text-left font-semibold">Received</th>
                <th className="py-1.5 px-3 text-left font-semibold">Method</th>
                <th className="py-1.5 px-3 text-left font-semibold">Reference</th>
                <th className="py-1.5 px-3 text-right font-semibold">Amount</th>
                <th className="py-1.5 px-3 text-right font-semibold" />
              </tr>
            </thead>
            <tbody>
              {ed.credits!.map((cr, i) => (
                <tr key={cr.id} className="border-b border-border last:border-0">
                  <td className="py-1.5 px-3 text-text-muted">{i + 1}</td>
                  <td className="py-1.5 px-3 whitespace-nowrap">{cr.date_money_received ?? '—'}</td>
                  <td className="py-1.5 px-3">{cr.collection_method ?? '—'}</td>
                  <td className="py-1.5 px-3 font-mono">{cr.collection_reference ?? '—'}</td>
                  <td className="py-1.5 px-3 text-right mono font-medium">{formatINR(cr.amount)}</td>
                  <td className="py-1.5 px-3 text-right">
                    {cr.has_receipt
                      ? <a href={`/api/applications/${ed.application_id}/lines/${cr.id}/receipt`} target="_blank" rel="noreferrer" className="text-primary hover:underline">receipt</a>
                      : <span className="text-text-muted">no receipt</span>}
                  </td>
                </tr>
              ))}
              {/* The parts must add up to what is being approved. Shown rather
                  than assumed — a mismatch is exactly what a checker is for. */}
              {(() => {
                const sum = ed.credits!.reduce((s, cr) => s + Number(cr.amount), 0);
                const total = Number(f.total_amount ?? 0);
                return (
                  <tr className="border-t border-border-strong">
                    <td className="py-1.5 px-3 text-text-muted" colSpan={4}>Total of the parts</td>
                    <td className={`py-1.5 px-3 text-right mono font-semibold ${Math.round(sum) === Math.round(total) ? '' : 'text-danger'}`}>{formatINR(sum)}</td>
                    <td className="py-1.5 px-3 text-right text-text-muted">
                      {Math.round(sum) === Math.round(total) ? '✓ matches' : `≠ ${formatINR(total)}`}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}
      <div className="mb-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
        <a href={`/app/customers/${ed.customer_id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">👤 View customer profile</a>
        {ed.has_receipt
          ? <a href={`/api/applications/${ed.application_id}/receipt`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              📎 View receipt / cheque photo{(ed.credits?.length ?? 0) > 1 ? ' (first part only)' : ''}
            </a>
          : <span className="text-text-muted">No receipt attached.</span>}
      </div>
      <div className="text-xs text-text-muted mb-2">
        Entered by the maker — correct anything that's wrong before approving.
        Interest starts from the money-received date (or the series deemed date, whichever is later).
      </div>
      <div className="grid grid-cols-2 gap-2">
        {FIELD_LABELS.map(([key, label, type]) => (
          <label key={key} className="text-xs">
            <span className="block text-text-muted mb-0.5">{label}</span>
            {key === 'referred_by_text' ? (
              <ReferredByPicker className={inp} value={String(f.referred_by_text ?? '')}
                onChange={(v) => setF({ ...f, referred_by_text: v })} />
            ) : key === 'collection_bank_id' ? (
              <select className={inp} value={f.collection_bank_id != null ? String(f.collection_bank_id) : ''}
                onChange={(e) => setF({ ...f, collection_bank_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">— not recorded —</option>
                {collectionBanks.map((b) => (
                  <option key={b.id} value={b.id}>{b.account_label}{b.account_number ? ` (${b.account_number})` : ''}</option>
                ))}
                {/* Keep an already-set account selectable even if it's since been
                    de-flagged/deactivated, so approving doesn't silently wipe it. */}
                {f.collection_bank_id != null && !collectionBanks.some((b) => b.id === f.collection_bank_id) && (
                  <option value={f.collection_bank_id}>current account #{f.collection_bank_id}</option>
                )}
              </select>
            ) : type === 'select' ? (
              /* Was a free text box, which is how the book ended up holding
                 NEFT, NEFT/RTGS, RTGS and neft as four different things
                 (owner 2026-08-04). The option list carries the record's OWN
                 stored value when it is not one of the canonical four, so
                 approving an "Other" or "Easebuzz" row leaves it untouched
                 instead of silently re-pointing it. */
              <select className={inp} value={String(f[key] ?? '')}
                onChange={(e) => setF({ ...f, [key]: e.target.value })}>
                {/* Only reachable when the maker left it blank — 3 live rows
                    have. Not a valid choice, so it cannot be picked back. */}
                {!String(f[key] ?? '').trim() && <option value="" disabled>Choose…</option>}
                {paymentMethodOptions(ed.fields[key] as string).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input className={inp} type={type} value={String(f[key] ?? '')}
                {...(key === 'total_amount' ? { min: LAKH, step: LAKH } : {})}
                onChange={(e) => setF({ ...f, [key]: type === 'number' ? Number(e.target.value) : e.target.value })} />
            )}
          </label>
        ))}
      </div>
      {err && <div className="text-xs text-danger mt-2">{err}</div>}
      {canAct && selfApproval && <SelfApprovalReason value={selfReason} onChange={setSelfReason} />}
      {canAct && (
        <div className="flex gap-2 items-center mt-3 border-t border-border pt-3">
          <button onClick={() => approveWithEdits.mutate()} disabled={approveWithEdits.isPending || blocked || !ticketOk}
            className="text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">
            {dirty ? `${actionLabel} (with corrections)` : actionLabel}
          </button>
          {dirty && <button onClick={() => { setErr(''); setF(ed.fields); }} className="text-xs text-text-muted hover:underline">Undo changes</button>}
          <span className="text-xs text-text-muted">
            {ticketOk ? 'Check the details above before confirming.'
              : `Investments are issued in units of ₹1,00,000 — correct the amount to ₹${(Math.max(1, Math.floor(Number(f.total_amount ?? 0) / LAKH)) * LAKH).toLocaleString('en-IN')} or ₹${((Math.floor(Number(f.total_amount ?? 0) / LAKH) + 1) * LAKH).toLocaleString('en-IN')} before approving.`}
          </span>
        </div>
      )}
    </div>
  );
}

/** Super Admin overriding the two-person rule on their own submission: the
 * reason is mandatory and is stored on the request + audit trail. */
function SelfApprovalReason({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="mt-2 bg-[color:var(--danger-bg)] border border-danger/40 rounded p-2.5">
      <div className="text-xs text-danger font-semibold mb-1">You submitted this request</div>
      <div className="text-xs text-text-muted mb-1.5">
        Approving your own submission bypasses the two-person rule. A reason is required and is recorded in the audit trail.
      </div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Reason for self-approval (required)"
        className="w-full px-2.5 py-1.5 text-xs border border-border-strong rounded outline-none focus:border-primary" />
    </div>
  );
}

/** Two-step confirm for requests that have no editable investment form.
 * `extraFields` are merged into the approve payload (e.g. an overridden
 * allotment date). */
function ConfirmApproval({ id, label, selfApproval, extraFields, onDone }: { id: number; label: string; selfApproval?: boolean; extraFields?: Record<string, unknown>; onDone: () => void }) {
  const qc = useQueryClient();
  const [err, setErr] = useState('');
  const [selfReason, setSelfReason] = useState('');
  const blocked = !!selfApproval && selfReason.trim().length < 3;
  const go = useMutation({
    mutationFn: () => {
      const extra: Record<string, unknown> = { ...(extraFields ?? {}) };
      if (selfApproval) extra.self_approval_reason = selfReason.trim();
      return api.post(`/api/approvals/${id}/approve`, Object.keys(extra).length ? { extra } : {});
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval', id] }); onDone(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed to approve'),
  });
  return (
    <div className="mt-2 border-t border-border pt-3">
      {selfApproval && <SelfApprovalReason value={selfReason} onChange={setSelfReason} />}
      <div className="flex gap-2 items-center mt-2">
      <button onClick={() => go.mutate()} disabled={go.isPending || blocked}
        className="text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">{label}</button>
      <span className="text-xs text-text-muted">Check the details above before confirming.</span>
      {err && <span className="text-xs text-danger">{err}</span>}
      </div>
    </div>
  );
}
